import Modal from 'components/Modal';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Input } from 'antd';
import styled from 'styled-components';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import Box from 'components/Box';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import { setLoadingPage } from 'services/UI/sagas';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import reportApis from 'features/report/services/apis';

export interface EditPrintCheckModalRef {
  open: (amount: number, id: string) => void;
}

interface EditPrintCheckModalProps {
  onSuccess?: () => void;
}

interface IEditPrintCheckFormValues {
  id: string;
  amount: number;
  note: string;
}

const EditPrintCheckModal = forwardRef<EditPrintCheckModalRef, EditPrintCheckModalProps>(({ onSuccess }, ref) => {
  const [form] = Form.useForm();
  const [visible, setOpen] = useState(false);

  const handleOpen = (amount: number, id: string) => {
    setOpen(true);
    form.setFieldsValue({ amount, id });
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({ open: handleOpen }));

  const handleFinish = async (values: any) => {
    const formValues = values as IEditPrintCheckFormValues;
    setLoadingPage(true);
    try {
      const res: IResponseDataBody = await reportApis.updatePrintCheckHistory({
        id: formValues.id,
        amount: formValues.amount,
        forNote: formValues.note,
      });

      if (res?.data?.data) {
        handleClose();
        toast.success('Check updated successfully');
        form.resetFields();
        onSuccess?.();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update check');
    } finally {
      setLoadingPage(false);
    }
  };

  return (
    <Modal
      visible={visible}
      v2
      modalTitle='EDIT PRINT CHECK AMOUNT'
      onClose={handleClose}
      width={800}
      noneBodyStyle
      noneFooterStyle
      onSubmit={() => form.submit()}

    >
      <FormContainer form={form} layout='vertical' onFinish={handleFinish}>
        <Box display='flex' flexDirection='column' p='3' gap='3'>
          <Box display='flex' flexDirection='column' gap='2'>
            <Text variant='CONTENT_2' color='text_3_v2' textAlign='center'>
              Amount 
            </Text>
            <Form.Item name='id' noStyle hidden>
              <Input />
            </Form.Item>
            <Form.Item name='amount' noStyle>
                <CurrencyInputPrecision className='amount-input'/>
            </Form.Item>
          </Box>
          <Box display='flex' flexDirection='column' gap='2'>
            <Text variant='H7'>Note</Text>
            <Form.Item name='note' noStyle>
              <Input.TextArea placeholder='Please enter...' autoSize={{ minRows: 3 }} />
            </Form.Item>
          </Box>
        </Box>
        <Form.Item noStyle name={'amount'}>
          <NumberPadV2 type={'NUMBER'} />
        </Form.Item>
      </FormContainer>
    </Modal>
  );
});

EditPrintCheckModal.displayName = 'EditPrintCheckModal';
export default EditPrintCheckModal;

const FormContainer = styled(Form)`
  display: flex;
  flex-direction: column;

  .amount-input {
    border: none;
    border-radius: 0;
    border-bottom: 1px solid ${colorTheme.v2_border_3};
    text-align: center;
    font-size: 32px;
    font-weight: 500;
    font-family: Poppins;
  }
`;
